import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { isTransactionGroupData } from 'api/Tickets/Types/TicketDetails'
import { TicketDto, TicketFilterKey } from 'api/Tickets/Types/TicketDto'
import { UpdateTicket } from 'api/Tickets/Types/UpdateTicket'
import { Modal } from 'components/CustomModal'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchCountriesAction } from 'store/identity/identity.actions'
import { getTicketStatusTypeAction } from 'store/tickets/tickets.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { ticketDetailsRouteUrl, ticketRouteUrl } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, FilterLogic } from 'utils/QueryHelpers'
import { uniqueArrayOfObjects } from 'utils/unique/unique'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { DocumentPreviewModal } from '../../Records/Clients/Details/ClientDetailsDocument/DocumentPreviewModal'
import { TicketsNavigation, TicketTab } from '../Navigation/TicketsNavigation'
import { TicketsViewType } from '../TicketsViewType'
import { TicketAssignee } from './TicketAssignee'
import { TicketDetailsForm } from './TicketDetailsForm'

interface TicketDetailsProps {
  viewType: TicketsViewType
}

export const TicketDetailsPage: React.FC<TicketDetailsProps> = ({ viewType }) => {
  const [crumbs, setCrumbs] = useState([ticketRouteUrl(viewType)])

  const [auth] = useContext(AuthSessionContext)
  const ticketsApiClient = useApiClient(TicketsApiClient)

  const [documentPreviewModal, setDocumentPreviewModal] = useState<null | {
    id: string
    name: string
  }>(null)
  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const [assigneeModal, setAssigneeModal] = useState<boolean>(false)
  const [, setGroupReferenceTickets] = useState<TicketDto[]>([])

  const params = useParams<{ id: string }>()
  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()
  const statuses = useAppSelector((state) => state.tickets.statuses)
  const countries = useAppSelector((state) => state.identity.countries)

  const identityApiClient = useApiClient(IdentityApiClient)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    async () => (params.id ? ticketsApiClient.getTicketDetails(params.id) : Promise.resolve(null)),
    []
  )
  const { data: ticketDetails, isLoading } = useFetchOne(fetchData)

  const usersData = useCallback(async () => {
    if (!ticketDetails) {
      return undefined
    }
    return await identityApiClient.getUsers(ticketDetails.departments)
  }, [ticketDetails]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: users } = useFetchOne(usersData)

  useEffect(() => {
    ;(async () => {
      if (isEmpty(statuses)) dispatch(getTicketStatusTypeAction(ticketsApiClient))
      if (isEmpty(countries)) dispatch(fetchCountriesAction(identityApiClient))
      if (isTransactionGroupData(ticketDetails?.data)) {
        const tickets = await ticketsApiClient.getTickets(null, {
          search: getReferenceTicketsQuery(),
          pageIndex: 1,
          pageSize: 100,
        })
        setGroupReferenceTickets(tickets.items)
      }
    })()
  }, [ticketDetails]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ticketDetails) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: ticketDetails.number.toString(),
          link: ticketDetailsRouteUrl(ticketDetails.id, viewType),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [ticketDetails, viewType]) // eslint-disable-line react-hooks/exhaustive-deps

  const getReferenceTicketsQuery = useCallback(
    () => {
      return (
        buildQuery({
          logic: FilterLogic.AND,
          filters: isTransactionGroupData(ticketDetails?.data)
            ? [
                {
                  field: TicketFilterKey.GROUP_ID,
                  operator: '==',
                  value: ticketDetails?.data.transaction.groupId,
                },
              ]
            : [],
        }) ?? null
      )
    },
    [ticketDetails] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleUpdate = async (values: UpdateTicket) => {
    await ticketsApiClient.updateTicket(values)
    setToast(successToast('Ticket updated successfully.'))
    forceRefresh()
  }

  const assignToTicketTypeTranslation = async (ticketId: string, assigneeId: string) => {
    await ticketsApiClient.addTicketTranslationTypeAssignee(ticketId, assigneeId)
    setAssigneeModal(false)
    setToast(successToast('Document Translation assignee added.'))
    forceRefresh()
  }

  const getAssigneeUsers = () => {
    if (ticketDetails?.assignee) {
      const isInUsers = (users || []).find((x) => x.id === ticketDetails.assignee?.id)
      if (isInUsers) {
        return users || []
      }
      const usersWithAssigned = (users || []).concat({
        id: ticketDetails.assignee.id,
        name: ticketDetails.assignee.name,
      })
      return uniqueArrayOfObjects(usersWithAssigned)
    }
    return users || []
  }

  const shouldBeTicketTypeTranslationAssigneeVisible = useMemo(() => {
    return (
      ticketDetails?.kind === 'document' &&
      !ticketDetails.relatesTo &&
      ticketDetails.status.id !== 4
    )
  }, [ticketDetails?.type]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <Spinner />
  }

  if (!ticketDetails || isEmpty(ticketDetails)) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          ticketId={params.id}
        />
      )}
      {ticketDetails && assigneeModal && (
        <Modal visible={assigneeModal} width={1100} onClose={() => setAssigneeModal(false)}>
          <TicketAssignee data={ticketDetails} onSubmit={assignToTicketTypeTranslation} />
        </Modal>
      )}
      {!!documentPreviewModal && (
        <DocumentPreviewModal
          document={documentPreviewModal}
          onClose={() => setDocumentPreviewModal(null)}
        />
      )}
      <Breadcrumbs crumbs={crumbs} />
      <TicketsNavigation data={ticketDetails} tab={TicketTab.DETAILS} />
      <TicketDetailsForm
        users={getAssigneeUsers()}
        data={ticketDetails}
        countries={countries}
        permissions={auth?.permissions || []}
        handleUpdate={handleUpdate}
        viewType={viewType}
        setDocumentPreviewModal={setDocumentPreviewModal}
        setAuditLogModal={setAuditLogVisible}
        setAssigneeModal={setAssigneeModal}
        shouldBeTicketTypeTranslationAssigneeVisible={shouldBeTicketTypeTranslationAssigneeVisible}
      />
    </React.Fragment>
  )
}
